import {
  Component,
  computed,
  forwardRef,
  inject,
  input,
  signal,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { SortDto, TenantFilterDto, TenantItemDto, TenantService } from '@artemis-software/wr-api';
import { firstValueFrom } from 'rxjs';
import { AbstractFormControl } from '@shared/form-controls/abstract-form-control';
import { NbComponentSize } from '@nebular/theme';
import { watch } from '@/utils/watch';
import { TenantTypePipe } from '@shared/pipes/tenant-type.pipe';
import OrderEnum = SortDto.OrderEnum;

@Component({
  standalone: false,
  selector: 'wr-tenant-multi-select',
  templateUrl: './tenant-multi-select.component.html',
  styleUrls: ['./tenant-multi-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TenantMultiSelectComponent),
      multi: true,
    },
  ],
})
export class TenantMultiSelectComponent extends AbstractFormControl<string[]> {
  private readonly tenantService = inject(TenantService);
  readonly tenantTypePipe = inject(TenantTypePipe);

  readonly size = input<NbComponentSize>('medium');
  readonly tenantType = input<TenantFilterDto.TypeEnum | undefined>();

  readonly resetVersion = signal(0);
  readonly filterText = signal('');
  readonly page = signal(0);
  readonly tenants = signal<TenantItemDto[]>([]);
  readonly selectedTenants = signal<TenantItemDto[]>([]);

  readonly placeholder = computed(() => `${this.tenantTypePipe.transform(this.tenantType())} auswählen`);
  readonly selectedNames = computed(() => this.selectedTenants().map(t => t.name).join(', '));
  readonly selectedIds = computed(() => new Set(this.selectedTenants().map(t => t.id)));

  constructor() {
    super();
    watch([this.tenantType, this.filterText], async () => {
      await this.reset();
    });
  }

  override writeValue(ids: string[] | undefined): void {
    super.writeValue(ids);
    if (ids?.length) {
      firstValueFrom(this.tenantService.findByIds(ids)).then(tenants => {
        this.selectedTenants.set(tenants);
      });
    } else {
      this.selectedTenants.set([]);
    }
  }

  isSelected(tenantId: string): boolean {
    return this.selectedIds().has(tenantId);
  }

  tenantIcon(type: TenantItemDto.TypeEnum): string {
    switch (type) {
      case 'Internal': return 'people-group';
      case 'External': return 'people-roof';
      case 'Contractor': return 'hammer';
      default: return 'people';
    }
  }

  toggleSelection(tenant: TenantItemDto): void {
    if (this.isSelected(tenant.id)) {
      this.removeSelection(tenant.id);
    } else {
      this.value = [...(this.value ?? []), tenant.id];
      this.selectedTenants.update(list => [...list, tenant]);
    }
  }

  removeSelection(tenantId: string): void {
    this.value = (this.value ?? []).filter(id => id !== tenantId);
    this.selectedTenants.update(list => list.filter(t => t.id !== tenantId));
  }

  async reset(): Promise<void> {
    this.resetVersion.update(v => v + 1);
    this.tenants.set([]);
    this.page.set(0);
    await this.loadMore();
  }

  async loadMore(): Promise<void> {
    const versionAtStart = this.resetVersion();
    const page = this.page();
    this.page.update(p => p + 1);

    const more = await firstValueFrom(this.tenantService.findAll({
      text: this.filterText(),
      type: this.tenantType(),
      page: page,
      size: 20,
      sort: { sort: 'name', order: OrderEnum.Asc },
    }));

    if (this.resetVersion() === versionAtStart) {
      this.tenants.update(current => [...current, ...more]);
    }
  }
}
